import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Pencil, Upload, Download, Trash2, Plus, FileText, X, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AthleteContextHeader from '../components/AthleteContextHeader';
import AthleteSearchFilter from '../components/AthleteSearchFilter';
import AcademicMultiProfileView from '../components/AcademicMultiProfileView';
import AcademicOverviewCards from '../components/academic/AcademicOverviewCards';
import SeasonSwitcher from '../components/SeasonSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffAcademic = ({ athleteId: propAthleteId }) => {
  const isScopedMode = !!propAthleteId;
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [academicProfile, setAcademicProfile] = useState(null);
  const [reports, setReports] = useState([]);
  const [academicProfiles, setAcademicProfiles] = useState([]);
  const [predefinedSubjects, setPredefinedSubjects] = useState([]);
  // Phase 75.2 — Full Academic Reports is season-aware. Defaults to the
  // current season; user can switch back via the SeasonSwitcher.
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [loading, setLoading] = useState(true);

  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [subjectMgmtOpen, setSubjectMgmtOpen] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState('');

  const [profileData, setProfileData] = useState({
    school_name: '', program_name: '', academic_year: '', status: 'good_standing'
  });

  const [reportData, setReportData] = useState({
    period: '', average_grade: '', teacher_comment: '', academy_comment: '', subjects: []
  });

  const [subjectInput, setSubjectInput] = useState({ subject_name: '', grade: '', comment: '' });

  const { user, hasModuleAccess } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const canEdit = user?.role === 'Admin' || user?.role === 'Staff';
  // Phase 75.2 — fine-grained delete for academic reports.
  // Admin always; Staff only when they have explicit `academic.delete`.
  const canDeleteReport = isAdmin || (user?.role === 'Staff' && hasModuleAccess('academic', 'delete'));
  // Phase 75.3 — move requires academic.edit (Admin gets it for free).
  const canMoveReport = isAdmin || (user?.role === 'Staff' && hasModuleAccess('academic', 'edit'));

  useEffect(() => {
    if (propAthleteId) {
      axios.get(`${API}/athletes/${propAthleteId}`).then(res => {
        setAthletes([res.data]);
        selectAthlete(res.data);
        setLoading(false);
      }).catch(() => { toast.error('Failed to fetch athlete'); setLoading(false); });
    } else {
      fetchMyAthletes();
    }
    fetchSubjects();
    // Phase 75.2 — load seasons + default to the current season so the Full
    // Academic Reports section shows the current term by default. Users can
    // switch back via SeasonSwitcher.
    axios.get(`${API}/seasons`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setSeasons(list);
      const current = list.find(s => s.is_current);
      if (current) setSelectedSeasonId(current.id);
    }).catch(() => { /* seasons optional — leave All Seasons */ });
  }, [propAthleteId]);

  const fetchMyAthletes = async () => {
    try {
      const response = await axios.get(`${API}/athletes`);
      setAthletes(response.data);
    } catch { toast.error('Failed to fetch athletes'); }
    finally { setLoading(false); }
  };

  const fetchSubjects = async () => {
    try {
      const res = await axios.get(`${API}/subjects`);
      setPredefinedSubjects(res.data);
    } catch { /* ignore */ }
  };

  const selectAthlete = async (athlete) => {
    setSelectedAthlete(athlete);
    await fetchAcademicData(athlete.id);
  };

  const fetchAcademicData = async (athleteId) => {
    try {
      const [profileRes, reportsRes, profilesRes] = await Promise.all([
        axios.get(`${API}/academic/athlete/${athleteId}`),
        axios.get(`${API}/academic-reports/athlete/${athleteId}`),
        // Phase 75.2 — multi-profile lookup so the Full Academic Reports
        // section can group entries by their academic_profile_id.
        axios.get(`${API}/academic/profiles?athlete_id=${athleteId}`),
      ]);
      setAcademicProfile(profileRes.data);
      setReports(reportsRes.data);
      setAcademicProfiles(Array.isArray(profilesRes.data) ? profilesRes.data : []);
    } catch (error) { console.error('Failed to fetch academic data:', error); }
  };

  const openProfileDialog = (isEdit = false) => {
    if (isEdit && academicProfile) {
      setProfileData({
        school_name: academicProfile.school_name,
        program_name: academicProfile.program_name || '',
        academic_year: academicProfile.academic_year,
        status: academicProfile.status
      });
      setEditingProfile(true);
    } else {
      setProfileData({ school_name: '', program_name: '', academic_year: '', status: 'good_standing' });
      setEditingProfile(false);
    }
    setProfileDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    if (!selectedAthlete) return;
    try {
      if (editingProfile && academicProfile) {
        await axios.put(`${API}/academic/${academicProfile.id}`, profileData);
        toast.success('Academic profile updated');
      } else {
        await axios.post(`${API}/academic`, { athlete_id: selectedAthlete.id, ...profileData });
        toast.success('Academic profile created');
      }
      setProfileDialogOpen(false);
      fetchAcademicData(selectedAthlete.id);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save profile'); }
  };

  const openReportDialog = (report = null) => {
    if (report) {
      setEditingReport(report);
      setReportData({
        period: report.period,
        average_grade: report.average_grade || '',
        teacher_comment: report.teacher_comment || '',
        academy_comment: report.academy_comment || '',
        subjects: report.subjects.map(s => ({ subject_name: s.subject_name, grade: s.grade, comment: s.comment || '' }))
      });
    } else {
      setEditingReport(null);
      setReportData({ period: '', average_grade: '', teacher_comment: '', academy_comment: '', subjects: [] });
    }
    setSubjectInput({ subject_name: '', grade: '', comment: '' });
    setReportDialogOpen(true);
  };

  const addSubjectFromPredefined = (subjectName) => {
    if (reportData.subjects.find(s => s.subject_name === subjectName)) {
      toast.error('Subject already added');
      return;
    }
    setReportData({
      ...reportData,
      subjects: [...reportData.subjects, { subject_name: subjectName, grade: '', comment: '' }]
    });
  };

  const addCustomSubject = () => {
    if (!subjectInput.subject_name) { toast.error('Subject name required'); return; }
    if (reportData.subjects.find(s => s.subject_name === subjectInput.subject_name)) {
      toast.error('Subject already added'); return;
    }
    setReportData({
      ...reportData,
      subjects: [...reportData.subjects, { ...subjectInput }]
    });
    setSubjectInput({ subject_name: '', grade: '', comment: '' });
  };

  const updateSubjectGrade = (index, field, value) => {
    const updated = [...reportData.subjects];
    updated[index] = { ...updated[index], [field]: value };
    setReportData({ ...reportData, subjects: updated });
  };

  const removeSubject = (index) => {
    setReportData({
      ...reportData,
      subjects: reportData.subjects.filter((_, i) => i !== index)
    });
  };

  const handleSaveReport = async () => {
    if (!selectedAthlete) return;
    try {
      if (editingReport) {
        await axios.put(`${API}/academic-reports/${editingReport.id}`, reportData);
        toast.success('Academic report updated');
      } else {
        await axios.post(`${API}/academic-reports`, { athlete_id: selectedAthlete.id, ...reportData });
        toast.success('Academic report created');
      }
      setReportDialogOpen(false);
      fetchAcademicData(selectedAthlete.id);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save report'); }
  };

  // Phase 75.2 — Admin (and Staff with academic.delete) can delete academic
  // reports. Backend re-validates the permission and emits an audit entry.
  const handleDeleteReport = async (report) => {
    if (!report?.id) return;
    if (!window.confirm('Delete this academic report? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/academic-reports/${report.id}`);
      toast.success('Academic report deleted');
      fetchAcademicData(selectedAthlete.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete report');
    }
  };

  // Phase 75.3 — Move a General/Unassigned report into one of the athlete's
  // existing academic profiles. Same report record, just a different
  // academic_profile_id. Audit logged server-side.
  const [moveDialog, setMoveDialog] = useState({ open: false, report: null, targetProfileId: '' });

  const openMoveDialog = (report) => {
    setMoveDialog({ open: true, report, targetProfileId: academicProfiles[0]?.id || '' });
  };

  const handleMoveReport = async () => {
    const { report, targetProfileId } = moveDialog;
    if (!report?.id || !targetProfileId) return;
    try {
      await axios.post(`${API}/academic-reports/${report.id}/move-profile`, {
        academic_profile_id: targetProfileId,
      });
      toast.success('Report moved to programme.');
      setMoveDialog({ open: false, report: null, targetProfileId: '' });
      fetchAcademicData(selectedAthlete.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move report');
    }
  };

  const handleUploadAttachment = async (reportId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.png,.jpg,.jpeg,.webp';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post(`${API}/academic-reports/${reportId}/attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Attachment uploaded');
        fetchAcademicData(selectedAthlete.id);
      } catch (error) { toast.error(error.response?.data?.detail || 'Upload failed'); }
    };
    input.click();
  };

  const handleDownloadAttachment = async (reportId, filename) => {
    try {
      const res = await axios.get(`${API}/academic-reports/${reportId}/attachment`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'school_report';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleDeleteAttachment = async (reportId) => {
    try {
      await axios.delete(`${API}/academic-reports/${reportId}/attachment`);
      toast.success('Attachment removed');
      fetchAcademicData(selectedAthlete.id);
    } catch { toast.error('Failed to remove attachment'); }
  };

  const handleAddSubject = async () => {
    if (!newSubjectName.trim()) return;
    try {
      await axios.post(`${API}/subjects`, { name: newSubjectName.trim() });
      toast.success(`Subject '${newSubjectName}' added`);
      setNewSubjectName('');
      fetchSubjects();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add subject'); }
  };

  const handleDeleteSubject = async (id) => {
    try {
      await axios.delete(`${API}/subjects/${id}`);
      toast.success('Subject removed');
      fetchSubjects();
    } catch { toast.error('Failed to delete subject'); }
  };

  const getStatusBadge = (status) => {
    const colors = {
      excellent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      good_standing: 'bg-blue-100 text-blue-800 border-blue-200',
      warning: 'bg-amber-100 text-amber-800 border-amber-200',
      probation: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || colors.good_standing;
  };

  const getStatusLabel = (s) => s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <AthleteContextHeader athleteId={propAthleteId} moduleName="Academic" />
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Academic Monitoring
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1">Track student-athlete academic performance</p>
          </div>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setSubjectMgmtOpen(true)} data-testid="manage-subjects-btn">
              Manage Subjects
            </Button>
          )}
        </div>

        {/* Phase 72.B — Overview cards (Admin/Staff only, hidden in scoped mode) */}
        {canEdit && !isScopedMode && <div className="mb-4"><AcademicOverviewCards /></div>}

        <div className="grid grid-cols-12 gap-6">
          {/* Athlete Sidebar */}
          {!isScopedMode && (
          <div className="col-span-12 lg:col-span-3">
            <Card data-testid="athlete-list">
              <CardHeader><CardTitle className="text-lg">My Athletes</CardTitle></CardHeader>
              <CardContent>
                <AthleteSearchFilter athletes={athletes} compact searchFirst>
                  {(filtered) => (
                    <div className="space-y-1 max-h-[500px] overflow-y-auto">
                      {filtered.map((athlete) => (
                        <button key={athlete.id} onClick={() => selectAthlete(athlete)}
                          className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm ${
                            selectedAthlete?.id === athlete.id
                              ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                          data-testid={`athlete-${athlete.id}`}
                        >
                          <span>{athlete.full_name}</span>
                          {athlete.squad && <span className="text-[10px] text-gray-400 ml-1">{athlete.squad}</span>}
                        </button>
                      ))}
                      {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-3">No athletes match</p>}
                    </div>
                  )}
                </AthleteSearchFilter>
              </CardContent>
            </Card>
          </div>
          )}

          {/* Main Content */}
          <div className={isScopedMode ? "col-span-12" : "col-span-12 lg:col-span-9"}>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : !selectedAthlete ? (
              <Card><CardContent className="py-12 text-center"><p className="text-gray-500">Select an athlete</p></CardContent></Card>
            ) : (
              <div className="space-y-6">
                {/* Phase 72.A — Multi-profile view (profiles, attendance, notes, timeline) */}
                <AcademicMultiProfileView athlete={selectedAthlete} canEdit={canEdit} />

                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center flex-wrap gap-2">
                      <div className="min-w-0">
                        <CardTitle data-testid="full-academic-reports-title">Full Academic Reports</CardTitle>
                        <p className="text-xs text-gray-500 mt-1" data-testid="full-academic-reports-helper">
                          View all academic reports across this athlete's academic profiles.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Phase 75.2 — season switcher; default = current season. */}
                        <SeasonSwitcher
                          value={selectedSeasonId}
                          onChange={setSelectedSeasonId}
                          adminOnly={false}
                          userRole={user?.role}
                          testId="full-academic-reports-season-switcher"
                        />
                        <Button onClick={() => openReportDialog()} data-testid="add-report-button">
                          <Plus className="w-4 h-4 mr-1" /> Add Report
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      // Phase 75.2 — season-aware filter. When a season is
                      // selected, keep only reports whose created_at falls in
                      // [start_date, end_date]; when empty, show all.
                      const season = seasons.find(s => s.id === selectedSeasonId) || null;
                      const inSeason = (report) => {
                        if (!season) return true;
                        const created = report.created_at ? String(report.created_at).slice(0, 10) : '';
                        if (!created) return true;
                        return created >= season.start_date && created <= season.end_date;
                      };
                      const visibleReports = reports.filter(inSeason);
                      if (visibleReports.length === 0) {
                        return (
                          <p className="text-gray-500 dark:text-gray-400 text-center py-8" data-testid="full-academic-reports-empty">
                            {season ? `No academic reports for ${season.name}` : 'No academic reports yet'}
                          </p>
                        );
                      }
                      // Group by academic_profile_id only — flat list of report
                      // cards under each programme heading (per user spec, no
                      // nested folders).
                      const profileNameOf = (id) => {
                        const p = academicProfiles.find(pp => pp.id === id);
                        return p ? (p.profile_name || p.school_name || 'Untitled profile') : null;
                      };
                      const profileTracksCompletion = (id) => {
                        const p = academicProfiles.find(pp => pp.id === id);
                        return !!p?.track_course_completion;
                      };
                      const groups = new Map();
                      const unassigned = [];
                      for (const r of visibleReports) {
                        const pid = r.academic_profile_id || null;
                        if (!pid || !profileNameOf(pid)) unassigned.push(r);
                        else {
                          if (!groups.has(pid)) groups.set(pid, []);
                          groups.get(pid).push(r);
                        }
                      }
                      const orderedGroups = [];
                      for (const p of academicProfiles) {
                        if (groups.has(p.id)) orderedGroups.push([p.id, groups.get(p.id)]);
                      }
                      if (unassigned.length > 0) orderedGroups.push([null, unassigned]);

                      const renderReportCard = (report) => (
                        <Card key={report.id} className="border" data-testid={`report-${report.id}`}>
                          <CardHeader className="pb-2">
                            <div className="flex justify-between items-start gap-3">
                              <div className="min-w-0">
                                <CardTitle className="text-lg" data-testid={`report-display-label-${report.id}`}>
                                  {profileNameOf(report.academic_profile_id)
                                    ? `${profileNameOf(report.academic_profile_id)} — ${report.period || 'Untitled period'}`
                                    : (report.period || 'Untitled report')}
                                </CardTitle>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                                  {report.average_grade && (
                                    <span className="text-xs text-emerald-700 dark:text-emerald-400">Avg Grade: <span className="font-semibold">{report.average_grade}</span></span>
                                  )}
                                  {profileTracksCompletion(report.academic_profile_id)
                                    && report.average_course_completion_percentage !== null
                                    && report.average_course_completion_percentage !== undefined && (
                                    <span className="text-xs text-sky-700 dark:text-sky-300" data-testid={`full-avg-completion-${report.id}`}>
                                      Avg Completion: {report.average_course_completion_percentage}%
                                    </span>
                                  )}
                                  {report.created_at && (
                                    <span className="text-xs text-gray-400">Created: {new Date(report.created_at).toLocaleDateString()}</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {report.attachment_filename ? (
                                  <div className="flex items-center gap-1">
                                    <Button size="sm" variant="outline" onClick={() => handleDownloadAttachment(report.id, report.attachment_filename)} data-testid={`download-attachment-${report.id}`}>
                                      <Download className="w-3 h-3 mr-1" /> {report.attachment_filename.length > 15 ? report.attachment_filename.slice(0,15)+'...' : report.attachment_filename}
                                    </Button>
                                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteAttachment(report.id)} data-testid={`delete-attachment-${report.id}`}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button size="sm" variant="outline" onClick={() => handleUploadAttachment(report.id)} data-testid={`upload-attachment-${report.id}`}>
                                    <Upload className="w-3 h-3 mr-1" /> Attach Report
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => openReportDialog(report)} data-testid={`edit-report-${report.id}`}>
                                  <Pencil className="w-3 h-3 mr-1" /> Edit
                                </Button>
                                {/* Phase 75.3 — Move to programme... only when
                                    the report is unassigned (no programme yet)
                                    AND the user can edit academic data. */}
                                {canMoveReport && !report.academic_profile_id && (
                                  <Button size="sm" variant="outline"
                                          onClick={() => openMoveDialog(report)}
                                          data-testid={`move-report-${report.id}`}>
                                    <FileText className="w-3 h-3 mr-1" /> Move to programme…
                                  </Button>
                                )}
                                {canDeleteReport && (
                                  <Button size="sm" variant="outline" className="text-red-600 hover:text-red-800 border-red-200 hover:border-red-300"
                                          onClick={() => handleDeleteReport(report)}
                                          data-testid={`delete-report-${report.id}`}>
                                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3 pt-2">
                            {report.subjects.length > 0 && (
                              <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subject Grades:</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {report.subjects.map((subject) => (
                                    <div key={subject.id} className="bg-gray-50 dark:bg-gray-800 p-2 rounded">
                                      <div className="flex justify-between">
                                        <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{subject.subject_name}</span>
                                        <span className="text-emerald-700 dark:text-emerald-400 font-semibold">{subject.grade}</span>
                                      </div>
                                      {subject.comment && <p className="text-xs text-gray-500 mt-1">{subject.comment}</p>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {report.teacher_comment && (
                              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                                <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">Teacher Comment</p>
                                <p className="text-sm text-blue-800 dark:text-blue-200">{report.teacher_comment}</p>
                              </div>
                            )}
                            {report.academy_comment && (
                              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3">
                                <p className="text-sm font-medium text-emerald-900 dark:text-emerald-300 mb-1">Academy Comment</p>
                                <p className="text-sm text-emerald-800 dark:text-emerald-200">{report.academy_comment}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );

                      return (
                        <div className="space-y-6" data-testid="reports-list">
                          {orderedGroups.map(([pid, list]) => {
                            const heading = pid ? profileNameOf(pid) : 'General / Unassigned';
                            const headingTestId = pid ? `reports-group-${pid}` : 'reports-group-unassigned';
                            return (
                              <div key={pid || 'unassigned'} data-testid={headingTestId}>
                                <div className="flex items-center gap-2 mb-2 border-b border-gray-200 dark:border-gray-700 pb-1">
                                  <FileText className="w-4 h-4 text-emerald-600" />
                                  <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200" data-testid={`reports-group-heading-${pid || 'unassigned'}`}>{heading}</h3>
                                  <span className="text-xs text-gray-400">({list.length} report{list.length === 1 ? '' : 's'})</span>
                                </div>
                                <div className="space-y-4">
                                  {list.map(renderReportCard)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>

        {/* Report Dialog */}
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="report-dialog">
            <DialogHeader>
              <DialogTitle>{editingReport ? 'Edit' : 'Create'} Academic Report</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Period</Label>
                  <Input value={reportData.period} onChange={(e) => setReportData({...reportData, period: e.target.value})} placeholder="e.g., Term 1 2026" data-testid="period-input" />
                </div>
                <div>
                  <Label>Average Grade</Label>
                  <Input value={reportData.average_grade} onChange={(e) => setReportData({...reportData, average_grade: e.target.value})} placeholder="e.g., 85%" data-testid="average-grade-input" />
                </div>
              </div>
              <div>
                <Label>Teacher Comment</Label>
                <Textarea value={reportData.teacher_comment} onChange={(e) => setReportData({...reportData, teacher_comment: e.target.value})} placeholder="Teacher's feedback..." data-testid="teacher-comment-input" />
              </div>
              <div>
                <Label>Academy Comment</Label>
                <Textarea value={reportData.academy_comment} onChange={(e) => setReportData({...reportData, academy_comment: e.target.value})} placeholder="Academy notes..." data-testid="academy-comment-input" />
              </div>

              {/* Predefined Subject Selection */}
              <div className="border-t pt-4">
                <Label className="mb-2 block">Subject Grades</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {predefinedSubjects
                    .filter(s => !reportData.subjects.find(rs => rs.subject_name === s.name))
                    .map(s => (
                      <Button key={s.id} size="sm" variant="outline" className="text-xs h-7" onClick={() => addSubjectFromPredefined(s.name)} data-testid={`add-predefined-${s.name}`}>
                        <Plus className="w-3 h-3 mr-1" /> {s.name}
                      </Button>
                    ))}
                </div>

                {/* Custom subject input */}
                <div className="flex gap-2 mb-3">
                  <Input className="flex-1" placeholder="Custom subject" value={subjectInput.subject_name}
                    onChange={(e) => setSubjectInput({...subjectInput, subject_name: e.target.value})} data-testid="subject-name-input" />
                  <Button size="sm" onClick={addCustomSubject} data-testid="add-subject-button">Add</Button>
                </div>

                {/* Subject list with grades */}
                {reportData.subjects.length > 0 && (
                  <div className="space-y-2" data-testid="subjects-list">
                    {reportData.subjects.map((subject, index) => (
                      <div key={index} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                        <span className="font-medium text-sm w-32 truncate text-gray-900 dark:text-gray-100">{subject.subject_name}</span>
                        <Input className="w-20" placeholder="Grade" value={subject.grade}
                          onChange={(e) => updateSubjectGrade(index, 'grade', e.target.value)} data-testid={`grade-input-${index}`} />
                        <Input className="flex-1" placeholder="Comment" value={subject.comment || ''}
                          onChange={(e) => updateSubjectGrade(index, 'comment', e.target.value)} data-testid={`comment-input-${index}`} />
                        <Button size="sm" variant="ghost" onClick={() => removeSubject(index)} data-testid={`remove-subject-${index}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleSaveReport} data-testid="submit-report">
                <Check className="w-4 h-4 mr-1" /> {editingReport ? 'Update' : 'Create'} Report
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 75.3 — Move Report dialog */}
        <Dialog open={moveDialog.open} onOpenChange={(o) => setMoveDialog((s) => ({ ...s, open: o }))}>
          <DialogContent data-testid="move-report-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle>Move report to programme</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {academicProfiles.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-300" data-testid="move-report-no-profiles">
                  Create an academic profile first before moving this report.
                </p>
              ) : (
                <>
                  <p className="text-xs text-gray-500" data-testid="move-report-helper">
                    Select the programme this report belongs to. The report keeps its content — only its programme link changes.
                  </p>
                  <div>
                    <Label>Programme</Label>
                    <Select
                      value={moveDialog.targetProfileId}
                      onValueChange={(v) => setMoveDialog((s) => ({ ...s, targetProfileId: v }))}
                    >
                      <SelectTrigger data-testid="move-report-profile-select">
                        <SelectValue placeholder="Select a programme" />
                      </SelectTrigger>
                      <SelectContent>
                        {academicProfiles.map((p) => (
                          <SelectItem key={p.id} value={p.id} data-testid={`move-report-option-${p.id}`}>
                            {p.profile_name || p.school_name || 'Untitled profile'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveDialog({ open: false, report: null, targetProfileId: '' })} data-testid="move-report-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleMoveReport}
                disabled={academicProfiles.length === 0 || !moveDialog.targetProfileId}
                data-testid="move-report-confirm"
              >
                Move report
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Profile Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogContent data-testid="profile-dialog">
            <DialogHeader>
              <DialogTitle>{editingProfile ? 'Edit' : 'Create'} Academic Profile</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>School Name</Label>
                <Input value={profileData.school_name} onChange={(e) => setProfileData({...profileData, school_name: e.target.value})} data-testid="school-name-input" />
              </div>
              <div>
                <Label>Program Name</Label>
                <Input value={profileData.program_name} onChange={(e) => setProfileData({...profileData, program_name: e.target.value})} placeholder="e.g., Science Track" data-testid="program-name-input" />
              </div>
              <div>
                <Label>Academic Year</Label>
                <Input value={profileData.academic_year} onChange={(e) => setProfileData({...profileData, academic_year: e.target.value})} placeholder="e.g., 2025-2026" data-testid="academic-year-input" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={profileData.status} onValueChange={(v) => setProfileData({...profileData, status: v})}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="good_standing">Good Standing</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="probation">Probation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleSaveProfile} data-testid="submit-profile">
                {editingProfile ? 'Update' : 'Create'} Profile
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Subject Management Dialog (Admin only) */}
        <Dialog open={subjectMgmtOpen} onOpenChange={setSubjectMgmtOpen}>
          <DialogContent data-testid="subject-mgmt-dialog">
            <DialogHeader>
              <DialogTitle>Manage Predefined Subjects</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="flex gap-2">
                <Input value={newSubjectName} onChange={(e) => setNewSubjectName(e.target.value)}
                  placeholder="New subject name" data-testid="new-subject-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSubject()} />
                <Button onClick={handleAddSubject} data-testid="add-new-subject-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1">
                {predefinedSubjects.map(s => (
                  <div key={s.id} className="flex justify-between items-center py-2 px-3 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{s.name}</span>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-7" onClick={() => handleDeleteSubject(s.id)} data-testid={`delete-subject-${s.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                {predefinedSubjects.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No subjects defined yet</p>}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StaffAcademic;
